import React from 'react';
import { Link } from 'react-router-dom';

// Import MUI icons for the builder options
import BuildIcon from '@mui/icons-material/Build';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';

export default function PlaybookBuilder() {
  return (
    <div className="page-wrapper">
      <div className="page-container">
        <header className="page-header">
          <div className="header-content">
            <h1>Build Your Playbook</h1>
            <p className="header-subtitle">Choose how you want to create your custom playbook</p>
          </div>
          <div className="header-stats">
            <div className="stat-card">
              <span className="stat-value">3</span>
              <span className="stat-label">Build Methods</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">Simple</span>
              <span className="stat-label">Recommended</span>
            </div>
          </div>
        </header>

        <div className="builder-options">
          <div className="builder-option active">
            <div className="builder-option-icon">
              <BuildIcon />
            </div>
            <h2>Simple Builder</h2>
            <p>Build your playbook by selecting from our pre-built strategies. Perfect for getting started with UFC betting.</p>
            <Link to="/playbooks/build/simple" className="builder-option-button">
              Get Started
            </Link>
          </div>

          <div className="builder-option active">
            <div className="builder-option-icon">
              <ChatIcon />
            </div>
            <h2>Language Powered Builder</h2>
            <p>Describe your betting strategy in plain English, and we'll turn it into a working playbook using AI.</p>
            <Link to="/playbooks/build/language" className="builder-option-button">
              Get Started
            </Link>
          </div>

          <div className="builder-option disabled">
            <div className="builder-option-icon">
              <SettingsIcon />
            </div>
            <div className="coming-soon-badge">
              <LockIcon />
              Coming Soon
            </div>
            <h2>Advanced Builder</h2>
            <p>Build complex, multi-strategy playbooks with custom rules, filters, and allocation methods.</p>
            <button className="builder-option-button" disabled>
              Coming Soon
            </button>
          </div>
        </div>

        <style jsx>{`
          .page-wrapper {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            background: var(--background);
            padding: 16px;
          }

          .page-container {
            flex: 1;
            max-width: 1200px;
            margin: 0 auto;
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 32px;
          }

          .page-header {
            background: linear-gradient(to right, rgba(123, 73, 209, 0.1), rgba(0, 0, 0, 0));
            border-radius: 16px;
            padding: 32px;
            margin-bottom: 32px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border: 1px solid rgba(123, 73, 209, 0.2);
          }

          .header-content {
            flex: 1;
          }

          .header-content h1 {
            font-size: 36px;
            font-weight: 700;
            margin: 0;
            background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0.8));
            -webkit-background-clip: text;
            background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .header-subtitle {
            font-size: 16px;
            color: rgba(255, 255, 255, 0.6);
            margin: 8px 0 0 0;
            line-height: 1.5;
          }

          .header-stats {
            display: flex;
            gap: 16px;
            margin-left: 32px;
          }

          .stat-card {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 12px;
            padding: 16px 24px;
            text-align: center;
            min-width: 120px;
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .stat-card .stat-value {
            display: block;
            font-size: 24px;
            font-weight: 600;
            color: var(--primary);
            margin-bottom: 4px;
          }

          .stat-card .stat-label {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.6);
          }

          .builder-options {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 24px;
            padding: 0;
          }

          .builder-option {
            background: var(--surface-light);
            border-radius: 16px;
            padding: 32px;
            text-align: center;
            position: relative;
            border: 1px solid rgba(123, 73, 209, 0.15);
            transition: all 0.2s ease;
          }

          .builder-option:hover:not(.disabled) {
            transform: translateY(-4px);
            border-color: var(--primary);
          }

          .builder-option.active {
            border-color: var(--primary);
          }

          .builder-option.disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .builder-option-icon {
            width: 48px;
            height: 48px;
            margin: 0 auto 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(123, 73, 209, 0.1);
            border-radius: 12px;
            color: var(--primary);
          }

          .builder-option h2 {
            font-size: 20px;
            font-weight: 600;
            margin: 0 0 16px;
            color: white;
          }

          .builder-option p {
            font-size: 14px;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.6);
            margin: 0 0 24px;
          }

          .builder-option-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 12px 24px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            text-decoration: none;
          }

          .builder-option-button:hover:not(:disabled) {
            background: #8b59e1;
            transform: translateY(-2px);
          }

          .builder-option-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }

          .coming-soon-badge {
            position: absolute;
            top: 16px;
            right: 16px;
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            background: rgba(0, 0, 0, 0.2);
            border-radius: 20px;
            font-size: 12px;
            color: rgba(255, 255, 255, 0.5);
          }

          @media (max-width: 768px) {
            .page-header {
              flex-direction: column;
              padding: 24px;
              text-align: center;
              gap: 24px;
            }

            .header-content h1 {
              font-size: 28px;
            }

            .header-subtitle {
              font-size: 14px;
            }

            .header-stats {
              margin-left: 0;
              width: 100%;
              justify-content: center;
            }

            .stat-card {
              min-width: 100px;
              padding: 12px 16px;
            }

            .builder-options {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
} 